import {
  Ban,
  BadgeCheck,
  Car,
  CheckCheck,
  CircleCheck,
  CircleDashed,
  CircleX,
  Clock,
  FileCheck,
  HandCoins,
  Handshake,
  Hourglass,
  Info,
  Lock,
  PenLine,
  Pencil,
  RotateCcw,
  Scale,
  Send,
  Shield,
  ShieldCheck,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import { z } from 'zod';

import type { Tone } from '@/shared/ui/badge';

import { textSchema, type PatternText } from './text';

/**
 * Os cinco tons de status da U19, "de quem é a vez": `neutral` rascunho ou cancelado, `info` em
 * aprovação (vez da UY3), `warn` aguardando ação externa ou revisão, `ok` deu certo, `err` parou por
 * problema. O mesmo tom significa a mesma coisa em qualquer tela (U39 §10).
 */
export const STATUS_TONES = ['neutral', 'info', 'warn', 'ok', 'err'] as const satisfies readonly Tone[];
export type StatusTone = (typeof STATUS_TONES)[number];

/**
 * Ícones de status por nome (kebab-case, o nome do Lucide), para a configuração ser JSON. Lista
 * fechada: ícone novo entra aqui, não na tela. Os nomes vêm do mapa de status do protótipo.
 */
export const STATUS_ICONS = {
  ban: Ban,
  'badge-check': BadgeCheck,
  car: Car,
  'check-check': CheckCheck,
  'circle-check': CircleCheck,
  'circle-dashed': CircleDashed,
  'circle-x': CircleX,
  clock: Clock,
  'file-check': FileCheck,
  'hand-coins': HandCoins,
  handshake: Handshake,
  hourglass: Hourglass,
  info: Info,
  lock: Lock,
  'pen-line': PenLine,
  pencil: Pencil,
  'rotate-ccw': RotateCcw,
  scale: Scale,
  send: Send,
  shield: Shield,
  'shield-check': ShieldCheck,
  'triangle-alert': TriangleAlert,
} as const satisfies Readonly<Record<string, LucideIcon>>;
export type StatusIconName = keyof typeof STATUS_ICONS;

const ICON_NAMES = Object.keys(STATUS_ICONS) as [StatusIconName, ...StatusIconName[]];

/** Rótulo, tom e ícone de um valor de status. O rótulo é traduzido pelo valor técnico (U33). */
export const statusEntrySchema = z.object({
  label: textSchema,
  tone: z.enum(STATUS_TONES),
  icon: z.enum(ICON_NAMES).optional(),
});

export interface StatusEntry {
  readonly label: PatternText;
  readonly tone: StatusTone;
  readonly icon?: StatusIconName | undefined;
}

/** Mapa valor técnico → status (`{ Draft: { label: msg\`Rascunho\`, tone: 'neutral', icon: 'pencil' } }`). */
export type StatusMap = Readonly<Record<string, StatusEntry>>;

/** Mapa valor técnico → rótulo traduzido, para enum sem tom. */
export type EnumMap = Readonly<Record<string, PatternText>>;
